import { useAuthData } from '@auth/shared/model';
import { useClerk, useSession, useUser } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { CircleUser, HelpCircle, Info, LogOut } from 'lucide-react';
import { useCallback, useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { joinCss } from '@/shared/lib/css-utils';
import { getInitials } from '@/shared/lib/get-initials';
import { Text } from '@/shared/ui';
import { ReleaseInfoModal } from './release-info-modal';
import css from './user-menu.module.scss';

type UserMenuProps = {
  className?: string;
};

export const UserMenu = ({ className }: UserMenuProps) => {
  const { signOut } = useClerk();
  const { session } = useSession();
  const { user: clerkUser } = useUser();
  const { data: authData } = useAuthData();
  const authUser = authData?.user;
  const signOutMutation = trpc.auth.signOut.useMutation();

  const firstName = authUser?.firstName ?? clerkUser?.firstName;
  const lastName = authUser?.lastName ?? clerkUser?.lastName;
  const email = authUser?.email ?? clerkUser?.primaryEmailAddress?.emailAddress;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);

  const handleSignOut = useCallback(async () => {
    if (session?.id) {
      await signOutMutation
        .mutateAsync({ sessionId: session.id })
        .catch(() => {});
    }
    signOut({ redirectUrl: '/sign-in' });
  }, [signOut, session?.id, signOutMutation]);

  const menuItems = [
    {
      icon: CircleUser,
      label: 'Profile',
      onClick: () => navigate({ to: '/profile' }),
    },
    {
      icon: HelpCircle,
      label: 'Help',
      onClick: () => navigate({ to: '/help' }),
    },
    {
      icon: Info,
      label: 'Release Info',
      onClick: () => setReleaseOpen(true),
    },
  ];

  return (
    <>
      {open && (
        <button
          className={css.backdrop}
          onClick={() => setOpen(false)}
          type="button"
        />
      )}
      <div className={css.root}>
        <button
          className={joinCss(css.trigger, className)}
          onClick={() => setOpen((prev) => !prev)}
          type="button"
        >
          {clerkUser?.hasImage ? (
            <img
              alt="Profile"
              className={css.avatarImage}
              src={clerkUser.imageUrl}
            />
          ) : (
            <div className={css.avatar}>
              {getInitials(firstName, lastName, email)}
            </div>
          )}
        </button>

        {open && (
          <div className={css.dropdown}>
            {menuItems.map(({ icon: Icon, label, onClick }) => (
              <button
                className={css.item}
                key={label}
                onClick={() => {
                  setOpen(false);
                  onClick();
                }}
                type="button"
              >
                <Icon className={css.itemIcon} />
                <Text as="span" size="sm">
                  {label}
                </Text>
              </button>
            ))}
            <div className={css.divider} />
            <button className={css.item} onClick={handleSignOut} type="button">
              <LogOut className={css.itemIcon} />
              <Text as="span" size="sm">
                Sign out
              </Text>
            </button>
          </div>
        )}
      </div>

      <ReleaseInfoModal
        onClose={() => setReleaseOpen(false)}
        open={releaseOpen}
      />
    </>
  );
};
